import { Component } from '@theme/component';

/**
 * Interactive hotspots section.
 *
 * Renders an image with numbered markers on top. Selecting a marker (or a
 * navigation dot) reveals the matching content panel on the side.
 *
 * @extends {Component<{
 *   marker: HTMLElement[];
 *   navDot: HTMLElement[];
 *   panel: HTMLElement[];
 * }>}
 */
class HotspotsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#setActive(this.#activeIndex);
  }

  #activeIndex = 0;

  /**
   * Handles a click on a marker or navigation dot.
   *
   * @param {Event} event
   */
  select = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const index = Number(target.dataset.index);
    if (Number.isNaN(index)) return;

    this.#setActive(index);
  };

  /**
   * Activates the hotspot at the given index and hides the others.
   *
   * @param {number} index
   */
  #setActive(index) {
    this.#activeIndex = index;

    const { marker = [], navDot = [], panel = [] } = this.refs;

    for (const group of [marker, navDot]) {
      for (const element of group) {
        const isActive = Number(element.dataset.index) === index;
        element.classList.toggle('is-active', isActive);
        element.setAttribute('aria-pressed', String(isActive));
      }
    }

    for (const element of panel) {
      const isActive = Number(element.dataset.index) === index;
      element.classList.toggle('is-active', isActive);
      element.toggleAttribute('hidden', !isActive);
    }
  }
}

if (!customElements.get('hotspots-component')) {
  customElements.define('hotspots-component', HotspotsComponent);
}
